// The device behind the number typed into the MQTT console.
//
// Both consoles (desktop and mobile) take a unique number and offer preset
// commands for it. This resolves that number to its install row so the console
// can draw the device's REAL controls beside the presets — the same
// `DeviceControlCard` the room grid and /device-admin use — and it watches the
// number in the Filtered pane so the reply is readable apart from the stream.
//
// One hook for both shells, for the same reason /device-admin embeds the card
// rather than drawing its own buttons: one implementation cannot drift.
//
// ⚠️ DEBOUNCED, because the number field updates on every keystroke and a
// unique number is six or seven characters long. Without the debounce a single
// number typed in costs six detail requests, five of them for numbers that do
// not exist. Nothing is fetched until the operator pauses.
//
// ── LIVE: the row is re-read while you watch ──────────────────────────────
// ⚠️ THE ROW WAS READ ONCE, so "Last code updated", the update mark and the
// heartbeat sat frozen at the moment the number was typed — while the log right
// beside it showed the device updating (reported 2026-10-06). The control
// card's buttons were live (they follow MQTT); the row facts were not.
//
// So the row is re-read:
//   • when the device TALKS — any message on `<uno>/client`, at most once per
//     MIN_GAP_MS. A press is a status message and can change nothing on the
//     row, but the gap keeps a busy device to one request per few seconds.
//   • after an UPDATE-shaped message, again at +15 s and +60 s. `CodeUpdated`
//     is stamped over HTTP when the update STARTS ("Updat-Switcher http…"), and
//     the device then flashes and reboots — its boot line ("WS,…") is the
//     sign the new image is running.
//   • every POLL_MS while the page is visible, for what MQTT never says
//     (another admin re-pointing the firmware, the server clearing the mark).
//
// ⚠️ FRESH, NEVER LOCAL-FIRST. On a phone `getDeviceDetail` answers from the
// offline cache — re-reading THAT would re-show the same stale row forever.
// ⚠️ REPLACED ONLY WHEN SOMETHING CHANGED, or every 30 s poll would hand the
// control card a new object and redraw it under the operator's finger.

import { useCallback, useEffect, useRef, useState } from 'react';
import { getDeviceDetailFresh } from '../../core/api/api';
import { addSub } from '../../core/mqtt/consoleSubs';
import { noteDeviceRow } from '../../core/devices/deviceLabels';
import { subscribeTopic, unsubscribeTopic, addMessageListener } from '../../core/mqtt/mqttClient';

const DEBOUNCE_MS = 450;
// Anything shorter is still being typed — no real unique number is this short.
const MIN_LEN = 5;

export const MIN_GAP_MS = 5000;
export const POLL_MS = 30000;
export const FOLLOW_UPS_MS = [15000, 60000];
// Messages that mean the row is about to change: the update command's echo,
// the firmware's download line, and the boot line a freshly flashed device sends.
export const UPDATE_SHAPED = /updatecode|updat-|\bWS,/i;

const first = (res) => (res && Array.isArray(res.data) ? res.data[0] : null) || null;

const validUno = (u) => {
  const uno = String(u || '').trim();
  return uno.length >= MIN_LEN && !uno.includes('/') ? uno : '';
};

/**
 * -> { device, loading, reload, refreshedAt }
 *
 * `device` is the row from GetDeviceDetailbyUniqueNumber, or null when the
 * number is blank, still being typed, unknown, or unreadable. A null is not an
 * error state for the caller — it simply draws no controls. `refreshedAt` is
 * when the row was last read from the server (ms), for a "read 4 s ago" line.
 */
export default function useConsoleDevice(uniqueNumber) {
  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshedAt, setRefreshedAt] = useState(null);
  // Bumped to re-read the row after something CHANGES it — the device type is
  // edited right beside the control card, and the card dispatches on DeviceID,
  // so it must redraw as the new type without the operator retyping the number.
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((n) => n + 1), []);

  const lastJson = useRef('');
  const lastReadAt = useRef(0);
  const unoRef = useRef('');

  // One read, applied only if it is still for the number on screen and only if
  // the row actually differs.
  const read = useCallback(async (uno) => {
    lastReadAt.current = Date.now();
    const row = await getDeviceDetailFresh(uno).then(first).catch(() => undefined);
    if (unoRef.current !== uno) return;
    // A failed request keeps what is on screen — a blip is not "no device".
    if (row === undefined) return;
    setRefreshedAt(Date.now());
    // A row with no DeviceID cannot be drawn — the card dispatches on it.
    const ok = row && row.DeviceID != null ? row : null;
    // Every caption for this number (breadcrumb, log lines) follows the row —
    // they were cached for the session and outlived an unassign.
    if (row) noteDeviceRow(row);
    const json = JSON.stringify(ok);
    if (json === lastJson.current) return;
    lastJson.current = json;
    setDevice(ok);
  }, []);

  // ── The number changed (or reload): first read, debounced ────────────────
  useEffect(() => {
    const uno = validUno(uniqueNumber);
    unoRef.current = uno;
    if (!uno) {
      lastJson.current = '';
      setDevice(null);
      setLoading(false);
      setRefreshedAt(null);
      return undefined;
    }

    let alive = true;
    setLoading(true);
    const timer = setTimeout(async () => {
      await read(uno);
      if (!alive) return;
      setLoading(false);
      /*
       * Auto-watch. The reply to a button press is the whole point of pressing
       * it on a bench, and in a stream of hundreds of messages from every other
       * device on the broker it scrolls past in a second. Watching the number
       * puts its traffic in the Filtered pane on its own. `addSub` is
       * idempotent and persists, exactly as adding it by hand does.
       */
      const json = lastJson.current;
      if (json && json !== 'null') {
        const row = JSON.parse(json);
        addSub(uno, row.DeviceName || uno);
      }
    }, DEBOUNCE_MS);

    return () => { alive = false; clearTimeout(timer); };
  }, [uniqueNumber, tick, read]);

  // ── Live: the device talks, and the clock ticks ──────────────────────────
  const hasDevice = !!device;
  useEffect(() => {
    const uno = validUno(uniqueNumber);
    if (!uno || !hasDevice) return undefined;
    const topic = `${uno}/client`;
    const timers = new Set();
    const later = (ms) => {
      const t = setTimeout(() => { timers.delete(t); read(uno); }, ms);
      timers.add(t);
    };
    let pending = null;
    const soon = () => {
      if (pending) return;
      const wait = Math.max(0, MIN_GAP_MS - (Date.now() - lastReadAt.current));
      pending = setTimeout(() => { pending = null; read(uno); }, wait);
    };

    subscribeTopic(topic);
    const off = addMessageListener((t, payload) => {
      if (t !== topic) return;
      soon();
      if (UPDATE_SHAPED.test(String(payload || ''))) FOLLOW_UPS_MS.forEach(later);
    });
    const poll = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      if (Date.now() - lastReadAt.current >= POLL_MS - 1000) read(uno);
    }, POLL_MS);

    return () => {
      off();
      unsubscribeTopic(topic);
      clearInterval(poll);
      if (pending) clearTimeout(pending);
      timers.forEach(clearTimeout);
    };
  }, [uniqueNumber, hasDevice, read]);

  return {
    device, loading, reload, refreshedAt,
  };
}
